/**
 * Etappe 2: Vorschläge aus der Altablage durchsehen und übernehmen.
 *
 * Was hier steht, ist **noch nichts**. Aus den Ordnernamen sind Kunden und
 * Spielplätze gelesen worden; angelegt wird erst, wenn Bernd auf
 * „Übernehmen" drückt — und zwar je Kunde, weil das die Einheit ist, über
 * die er entscheiden kann: kennt er den Kunden, kennt er auch dessen
 * Plätze.
 *
 * Zwei Dinge sind Absicht:
 *
 * **Unsicheres wird gezeigt, nicht versteckt.** Was sich nicht eindeutig
 * lesen ließ, trägt ein Fragezeichen und den Grund. Bei Prüfdaten ist ein
 * leeres Feld besser als ein falscher Wert — und ein sichtbarer Zweifel
 * besser als ein stiller.
 *
 * **Die Fundstelle steht dabei.** Bei einem Zweifel muss nachsehbar sein,
 * woher eine Angabe kommt; „aus dem Ordnernamen" ist keine Quelle,
 * `A Kundenakten/TÜV/TÜV Emden Hochschule` ist eine.
 */

import { useState } from 'react'
import {
  type Kundenblock,
  useVorschlaege,
  useVorschlaegeSammeln,
  useVorschlaegeUebernehmen,
  useVorschlaegeVerwerfen,
} from '../api/inventur'
import { ApiFehler } from '../api/fehler'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis } from '../bausteine/Hinweise'

export function Altbestand({ laufId }: { laufId: string }) {
  const liste = useVorschlaege(laufId)
  const sammeln = useVorschlaegeSammeln(laufId)
  const uebernehmen = useVorschlaegeUebernehmen(laufId)
  const verwerfen = useVorschlaegeVerwerfen(laufId)
  const [nurOffene, setzeNurOffene] = useState(true)
  const [ordner, setzeOrdner] = useState('')
  const [nurUnsichere, setzeNurUnsichere] = useState(false)

  if (liste.isLoading) return <Ladeanzeige />

  const daten = liste.data
  // Auch dann warten, wenn der Server gerade »läuft schon« gemeldet hat:
  // sonst stünde die Anzeige still, bis jemand neu lädt.
  const laeuft =
    daten?.status === 'laeuft' || (sammeln.error != null && schonAmLaufen(sammeln.error))
  const bloecke = (daten?.bloecke ?? []).filter((b) => {
    if (nurOffene && b.kunde.stand !== 'offen') return false
    if (nurUnsichere && b.zu_pruefen === 0) return false
    return true
  })

  return (
    <div className="space-y-4">
      <div className="karte space-y-3">
        <h2 className="text-xl font-bold">Kunden und Plätze aus den Ordnern</h2>
        <p className="text-base text-slate-700">
          Gelesen werden nur Ordnernamen — kein Dokument wird geöffnet.
          Angelegt wird nichts, bis Sie es je Kunde übernehmen.
        </p>
        {/* Ein eigener Ordner für diesen Schritt. Die Inventur sieht sich
            den ganzen Baum an; die Kundenordner liegen in einem Zweig
            davon, und den ganzen Baum zu lesen dauert Minuten für nichts. */}
        <label htmlFor="alt-ordner">Ordner mit den Kundenakten</label>
        <input
          id="alt-ordner"
          value={ordner}
          placeholder={daten?.ordner || 'Import/Spielplatzprüfungen/A Kundenakten'}
          onChange={(e) => setzeOrdner(e.target.value)}
        />

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="knopf-zweit"
            disabled={sammeln.isPending || laeuft}
            onClick={() => sammeln.mutate(ordner || undefined)}
          >
            {laeuft ? 'Liest …' : daten?.kunden ? 'Neu einlesen' : 'Ordner einlesen'}
          </button>
          {laeuft && (
            <span aria-live="polite" className="text-base text-slate-700">
              Das dauert bei einer gewachsenen Ablage etwa eine halbe Minute.
              Der Lauf geht weiter, auch wenn Sie die Seite verlassen.
            </span>
          )}
          {daten && daten.kunden > 0 && (
            <span className="text-base text-slate-700">
              {daten.kunden} Kunden · {daten.plaetze} Spielplätze ·{' '}
              <span className="font-semibold">{daten.zu_pruefen} zu prüfen</span> ·{' '}
              {daten.offen} noch offen
            </span>
          )}
        </div>
        {/* Ein 409 heißt nicht »kaputt«, sondern »läuft schon«. Als
            roter Kasten gelesen wirkt er wie ein Fehler; hier ist er eine
            Auskunft, und die Anzeige wartet dann einfach mit. */}
        <Fehlerhinweis
          fehler={
            sammeln.error && !schonAmLaufen(sammeln.error)
              ? sammeln.error
              : (uebernehmen.error ?? verwerfen.error)
          }
        />
        {sammeln.error && schonAmLaufen(sammeln.error) && (
          <p role="status" className="text-base text-slate-700">
            Es läuft bereits ein Einlesen — die Anzeige holt es sich, sobald
            es fertig ist.
          </p>
        )}
        {daten?.status === 'gescheitert' && daten.fehler && (
          <p role="alert" className="rounded-feld border-2 border-stufe-3 bg-stufe-3-grund p-2 text-base text-stufe-3">
            Einlesen gescheitert: {daten.fehler}
          </p>
        )}
        {uebernehmen.data && (
          <p role="status" className="marke inline-block bg-stufe-ob-grund text-stufe-ob">
            {uebernehmen.data.kunden_neu} Kunden und {uebernehmen.data.plaetze_neu}{' '}
            Spielplätze angelegt
            {uebernehmen.data.kunden_bekannt + uebernehmen.data.plaetze_bekannt > 0 &&
              `, ${uebernehmen.data.kunden_bekannt + uebernehmen.data.plaetze_bekannt} gab es schon`}
            .
          </p>
        )}
      </div>

      {daten && daten.kunden > 0 && (
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={nurOffene}
              onChange={(e) => setzeNurOffene(e.target.checked)}
            />
            <span className="text-base font-normal">Nur noch nicht übernommene</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={nurUnsichere}
              onChange={(e) => setzeNurUnsichere(e.target.checked)}
            />
            <span className="text-base font-normal">Nur die zu prüfenden</span>
          </label>
        </div>
      )}

      {bloecke.length === 0 ? (
        <Leerhinweis
          titel={daten?.kunden ? 'Nichts übrig' : 'Noch nichts eingelesen'}
          text={
            daten?.kunden
              ? 'Mit dieser Auswahl bleibt nichts stehen.'
              : 'Auf „Ordner einlesen" drücken. Es wird dabei nichts angelegt.'
          }
        />
      ) : (
        <ul className="space-y-3">
          {bloecke.map((b) => (
            <Block
              key={b.kunde.id}
              block={b}
              laeuft={uebernehmen.isPending || verwerfen.isPending}
              beimUebernehmen={(ids) => uebernehmen.mutate(ids)}
              beimVerwerfen={(ids) => verwerfen.mutate(ids)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function Block({
  block,
  laeuft,
  beimUebernehmen,
  beimVerwerfen,
}: {
  block: Kundenblock
  laeuft: boolean
  beimUebernehmen: (ids: string[]) => void
  beimVerwerfen: (ids: string[]) => void
}) {
  const [offen, setzeOffen] = useState(false)
  // Welche Plätze mitkommen. Voreingestellt alle – wer einen nicht will,
  // nimmt ihn heraus; das ist der seltenere Fall.
  const [gewaehlt, setzeGewaehlt] = useState<Set<string>>(
    () => new Set(block.plaetze.map((p) => p.id)),
  )
  const uebernommen = block.kunde.stand === 'uebernommen'
  const verworfen = block.kunde.stand === 'verworfen'

  return (
    <li
      className={`karte space-y-2 ${verworfen ? 'opacity-50' : ''} ${
        block.zu_pruefen > 0 ? 'border-stufe-1' : ''
      }`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-bold">
            {!block.kunde.sicher && (
              <span aria-label="bitte prüfen" title="bitte prüfen">
                ?{' '}
              </span>
            )}
            {block.kunde.name}
          </div>
          <div className="text-base text-slate-700">
            {block.kunde.plz ?? 'keine Postleitzahl'} · {block.plaetze.length}{' '}
            {block.plaetze.length === 1 ? 'Spielplatz' : 'Spielplätze'} ·{' '}
            {block.dateien} Dateien
          </div>
          {block.kunde.hinweis && (
            <p className="mt-1 text-base text-stufe-1">{block.kunde.hinweis}</p>
          )}
          {block.kunde.quellen?.[0] && (
            <p className="mt-1 break-all text-sm text-slate-500">
              {block.kunde.quellen[0]}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          {uebernommen ? (
            <span className="marke bg-stufe-ob-grund text-stufe-ob">übernommen</span>
          ) : verworfen ? (
            <span className="marke bg-slate-200 text-slate-700">verworfen</span>
          ) : (
            <>
              <button
                type="button"
                className="knopf-haupt px-3 text-base"
                disabled={laeuft}
                onClick={() => beimUebernehmen([block.kunde.id, ...gewaehlt])}
              >
                Übernehmen
              </button>
              <button
                type="button"
                className="knopf-still px-3 text-base"
                disabled={laeuft}
                onClick={() =>
                  beimVerwerfen([block.kunde.id, ...block.plaetze.map((p) => p.id)])
                }
              >
                Verwerfen
              </button>
            </>
          )}
        </div>
      </div>

      {block.plaetze.length > 0 && (
        <>
          <button
            type="button"
            className="knopf-still px-3 text-base"
            aria-expanded={offen}
            onClick={() => setzeOffen((o) => !o)}
          >
            {offen
              ? 'Plätze verbergen'
              : `${gewaehlt.size} von ${block.plaetze.length} Plätzen`}
          </button>

          {offen && (
            <ul className="space-y-1 border-t-2 border-slate-200 pt-2">
              {block.plaetze.map((p) => (
                <li key={p.id}>
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5"
                      disabled={uebernommen || verworfen}
                      checked={gewaehlt.has(p.id)}
                      onChange={(e) =>
                        setzeGewaehlt((alt) => {
                          const neu = new Set(alt)
                          if (e.target.checked) neu.add(p.id)
                          else neu.delete(p.id)
                          return neu
                        })
                      }
                    />
                    <span className="min-w-0 flex-1 font-normal">
                      <span className="block">
                        {!p.sicher && <span title="bitte prüfen">? </span>}
                        {p.name}
                        <span className="text-slate-600"> · {p.dateien} Dateien</span>
                      </span>
                      {p.hinweis && (
                        <span className="block text-base text-stufe-1">{p.hinweis}</span>
                      )}
                      {p.quellen?.[0] && (
                        <span className="block break-all text-sm text-slate-500">
                          {p.quellen[0]}
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  )
}

/** Meldet der Server, dass schon gelesen wird? */
function schonAmLaufen(fehler: unknown): boolean {
  return fehler instanceof ApiFehler && fehler.status === 409
}
